import React, { useEffect, useState } from 'react';
import Filters from '../components/Filters';
import { useData } from '../context/DataContext';
import Card from '../components/Card';
import { useFavorites } from '../context/FavoritesContext';
import Carousel from '../components/Carousel';
import TagsHoc from '../components/hoc/TagsHoc';
import { useQueryStrings } from '../context/QueryStringContext';
import Button from '../components/Button';
import Popup from '../components/Popup';
import Calendar from '../components/Calendar';
import { notFalsy, notUndefined, onlyUnique } from '../../../../../../../shared/lib/functional';
import { useGlobalControls } from '../context/GlobalControlsContext';
import { isoDate } from '../../../../../../../shared/lib/time';
import { isDateDisabled } from '../../../../../../../shared/lib/dateUtils';
import { useApp } from '../context/AppContext';
import { getOptimizedEdpImageUrl } from '../../../../../../../shared/lib/image';

function LineupPage() {
  const { dateLineup, todayLineup, data } = useData();
  const { favorites, toggleFavorite } = useFavorites();
  const { qs, updateQs } = useQueryStrings();
  const { toggleOverflow } = useGlobalControls();
  const { config } = useApp();

  const [showCalendar, setShowCalendar] = useState(false);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  const todayDate = new Date(config.date);
  const date = qs.date;
  const currentDate = new Date(date);

  const tomorrowDate = new Date(todayDate);
  tomorrowDate.setUTCDate(tomorrowDate.getUTCDate() + 1);
  const tomorrow = isoDate(tomorrowDate);

  const prevDate = new Date(currentDate);
  prevDate.setUTCDate(prevDate.getUTCDate() - 1);

  const nextDate = new Date(currentDate);
  nextDate.setUTCDate(nextDate.getUTCDate() + 1);

  const disablePrev = isDateDisabled(prevDate, todayDate, data.appConfig.FUTURE_DAY_CAP);
  const disableNext = isDateDisabled(nextDate, todayDate, data.appConfig.FUTURE_DAY_CAP);

  const filteredEvents = dateLineup.events.filter(event => {
    if (activeFilters.length === 0) return true;

    let filterCheck = true;

    activeFilters.forEach(filter => {
      switch (filter) {
        case data.appConfig.TRENDING_LABEL: {
          filterCheck = filterCheck && event.isTrending;
          break;
        }
        case data.appConfig.UPCOMING_LABEL: {
          filterCheck = filterCheck && event.isUpcoming;
          break;
        }
        default: {
          if (data.collections.attributes[filter]) {
            filterCheck =
              filterCheck &&
              (event.attributeIds.length === 0 || event.attributeIds.includes(filter));
          } else if (data.collections.zones[filter]) {
            filterCheck =
              filterCheck && (event.zoneIds.length === 0 || event.zoneIds.includes(filter));
          }
          break;
        }
      }
    });

    return filterCheck;
  });

  function handleDateChange(date: Date) {
    const formattedDate = date.toISOString().split('T')[0];
    updateQs('date', formattedDate);
    setShowCalendar(false);
  }

  function handleDateIncrement(date: Date, direction: 1 | -1) {
    const newDate = new Date(date);
    newDate.setUTCDate(newDate.getUTCDate() + direction);

    if (isDateDisabled(newDate, new Date(todayLineup.date), data.appConfig.FUTURE_DAY_CAP)) {
      return;
    }

    const formattedDate = newDate.toISOString().split('T')[0];
    updateQs('date', formattedDate);
    setShowCalendar(false);
  }

  useEffect(() => {
    toggleOverflow('calendar', showCalendar);
  }, [showCalendar]);

  const zoneKeys = Object.keys(data.collections.zones).map(e => data.collections.zones[e]._id);

  const attributeKeys = dateLineup.events
    .map(e => e.attributeIds)
    .flat()
    .filter(notFalsy)
    .filter(onlyUnique);

  return (
    <div id="LineupPage">
      {showCalendar && (
        <Popup onClose={() => setShowCalendar(false)}>
          <Calendar selectedDate={new Date(date)} onDateChange={handleDateChange} />
        </Popup>
      )}
      <Filters
        title={data.appConfig.LINEUP_LABEL}
        icon={['far', 'calendar-day']}
        staticButtons={[
          todayLineup.events.find(e => e.isTrending) ? data.appConfig.TRENDING_LABEL : undefined,
          todayLineup.date === dateLineup.date && todayLineup.events.find(e => e.isUpcoming)
            ? data.appConfig.UPCOMING_LABEL
            : undefined,
        ].filter(notUndefined)}
        keys={[...zoneKeys, ...attributeKeys]}
        onFilterChange={setActiveFilters}
        labelExtractor={(key: string) =>
          data.collections.attributes[key]?.name || data.collections.zones[key]?.name
        }
        rightSide={
          <div className="flex items-center -mr-4">
            <Button
              className={`font-semibold text-blue-500 py-3 px-4 -my-3 pr-0 date-picker ${
                disablePrev ? 'opacity-50 pointer-events-none' : ''
              }`}
              icon={['fass', 'arrow-left']}
              onClick={() => handleDateIncrement(new Date(date), -1)}
              disabled={disablePrev}
            />
            <Button
              className="font-semibold text-blue-500 py-3 px-4 -my-3 date-picker"
              onClick={() => setShowCalendar(true)}
            >
              {date === todayLineup.date
                ? data.appConfig.TODAY_LABEL
                : date === tomorrow
                  ? data.appConfig.TOMORROW_LABEL
                  : new Date(date).toLocaleDateString(qs.lang, {
                      month: 'short',
                      day: 'numeric',
                      weekday: 'short',
                      timeZone: 'UTC',
                    })}
            </Button>
            <Button
              className={`font-semibold text-blue-500 py-3 px-4 -my-3 pl-0 date-picker ${
                disableNext ? 'opacity-50 pointer-events-none' : ''
              }`}
              icon={['fass', 'arrow-right']}
              onClick={() => handleDateIncrement(new Date(date), 1)}
              disabled={disableNext}
            />
          </div>
        }
      />
      <div className="grid gap-2 pb-8">
        {filteredEvents.map((evt, i) => {
          return (
            <TagsHoc type="event" item={evt} key={evt.id}>
              {props => (
                <>
                  <Card
                    className="border-b"
                    onClick={() => updateQs('event', evt.id)}
                    title={evt.title}
                    subtitle={[evt.venueString, evt.timeString]}
                    image={getOptimizedEdpImageUrl(
                      evt.images,
                      evt.imageOptimizations,
                      config.imgRequirements,
                      0
                    )}
                    tags={props.tags}
                    isFavorite={!!favorites.events[evt.id]}
                    onFavoriteClick={() => toggleFavorite({ type: 'events', value: evt })}
                  />
                  {i === 1 &&
                    activeFilters.length === 0 &&
                    todayLineup.offers.filter(o => o.isTrending).length > 0 &&
                    filteredEvents.length > 2 &&
                    config.enabledViews.includes('offers') && (
                      <Carousel
                        title="Special Offers"
                        icon={['far', 'hand-holding-circle-dollar']}
                        className="-my-2 carousel-container py-4"
                      >
                        {todayLineup.offers
                          .filter(o => o.isTrending)
                          .map(offer => (
                            <TagsHoc type="basic" item={offer} key={offer.id}>
                              {props => (
                                <Card
                                  className="carousel border"
                                  onClick={() => updateQs('offer', offer.id)}
                                  title={offer.title}
                                  subtitle={offer.description}
                                  image={getOptimizedEdpImageUrl(
                                    offer.images,
                                    offer.imageOptimizations,
                                    config.imgRequirements,
                                    0
                                  )}
                                  tags={props.tags}
                                  isFavorite={!!favorites.offers[offer.id]}
                                  onFavoriteClick={() =>
                                    toggleFavorite({ type: 'offers', value: offer })
                                  }
                                />
                              )}
                            </TagsHoc>
                          ))}
                      </Carousel>
                    )}
                </>
              )}
            </TagsHoc>
          );
        })}

        {filteredEvents.length <= 0 && (
          <div className="italic text-slate px-4 pt-4 text-center no-text-footer">
            {data.appConfig.NO_RESULTS_TEXT}
          </div>
        )}
      </div>
    </div>
  );
}

export default LineupPage;
